import { ForwardedRef, forwardRef, ReactNode } from 'react';
import { SheetClose, SheetCloseProps } from './_sheet_close.component';
import { SheetContent, SheetContentProps } from './_sheet_content.component';
import { SheetRoot, SheetRootProps } from './_sheet_root.component';
import { SheetTrigger, SheetTriggerProps } from './_sheet_trigger.component';

const CompoundSheet = {
	Root: forwardRef(SheetRoot),
	Trigger: forwardRef(SheetTrigger),
	Content: forwardRef(SheetContent),
	Close: forwardRef(SheetClose),
} as const;

interface ImperativeSheetRef {
	open(): void;
	close(): void;
}

interface SheetProps extends SheetRootProps {
	children: ReactNode;

	/**
	 * The element that will open the sheet.
	 * E.g. `<button>Click me</button>`
	 */
	trigger?: JSX.Element;
}

function CellulaSheet({ trigger, children, ...rootProps }: SheetProps, ref: ForwardedRef<ImperativeSheetRef>): JSX.Element {
	return (
		<CompoundSheet.Root {...rootProps} ref={ref}>
			{trigger ? <CompoundSheet.Trigger asChild>{trigger}</CompoundSheet.Trigger> : null}

			<CompoundSheet.Content>{children}</CompoundSheet.Content>
		</CompoundSheet.Root>
	);
}

type ForwardRefType<T, R> = React.ForwardRefExoticComponent<T & React.RefAttributes<R>>;

interface SheetComponent extends ForwardRefType<SheetProps, ImperativeSheetRef> {
	Root: ForwardRefType<SheetRootProps, ImperativeSheetRef>;
	Trigger: ForwardRefType<SheetTriggerProps, HTMLButtonElement>;
	Content: ForwardRefType<SheetContentProps, HTMLDivElement>;
	Close: ForwardRefType<SheetCloseProps, HTMLButtonElement>;
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A sheet is a dialog that slides in from the edge of the screen.
 */
const Sheet = forwardRef(CellulaSheet) as SheetComponent;
Sheet.Root = CompoundSheet.Root;
Sheet.Trigger = CompoundSheet.Trigger;
Sheet.Content = CompoundSheet.Content;
Sheet.Close = CompoundSheet.Close;

export type { ImperativeSheetRef };
export { Sheet };
